import { Link } from 'react-router-dom';
import type { Drama } from '@/types/content';

interface DramaCardProps {
  drama: Drama;
  rank?: number;
}

export default function DramaCard({ drama, rank }: DramaCardProps) {
  const progress = Math.round((drama.updatedEpisodes / drama.totalEpisodes) * 100);
  const isFinished = drama.status === '已完结';

  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors duration-200 hover:border-primary-300">
      <Link to={`/drama/${drama.id}`} className="relative block cursor-pointer">
        <div className="relative h-[300px] w-full overflow-hidden bg-background-200 md:h-[340px]">
          <img
            src={drama.cover}
            alt={`短剧《${drama.title}》海报`}
            title={`${drama.title} 连续剧集更新顺序与分集剧情介绍`}
            className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-x-0 top-0 flex items-start justify-between p-3">
            <span
              className={`rounded-md px-2 py-1 text-xs ${
                isFinished
                  ? 'bg-secondary-900/85 text-background-100'
                  : 'bg-primary-500/95 text-background-50'
              }`}
            >
              {drama.status}
            </span>
            {typeof rank === 'number' && (
              <span className="font-display flex h-7 w-7 items-center justify-center rounded-md bg-accent-500 text-sm text-secondary-950">
                {rank}
              </span>
            )}
          </div>
          <span className="absolute bottom-3 left-3 rounded-md bg-background-50/90 px-2 py-1 text-xs text-foreground-700 backdrop-blur-sm">
            {drama.category}
          </span>
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="font-heading text-base font-bold text-foreground-950 md:text-lg">
          <Link to={`/drama/${drama.id}`} className="cursor-pointer transition-colors hover:text-primary-600">
            {drama.title}
          </Link>
        </h3>
        <p className="mt-1 line-clamp-1 text-xs text-foreground-500">{drama.subtitle}</p>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {drama.tags.map((tag) => (
            <span key={tag} className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">
              {tag}
            </span>
          ))}
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-foreground-600">
            <span>更新至 {drama.updatedEpisodes} / {drama.totalEpisodes} 集</span>
            <span className="text-accent-700">{progress}%</span>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
            <div className="h-full rounded-full bg-primary-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-star-fill text-accent-500" aria-hidden="true" />
            {drama.rating.toFixed(1)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-fire-line text-primary-500" aria-hidden="true" />
            {(drama.heat / 10000).toFixed(1)} 万热度
          </span>
          <Link
            to={`/drama/${drama.id}`}
            className="cursor-pointer text-primary-600 transition-colors hover:text-primary-700"
          >
            分集介绍
            <i className="ri-arrow-right-s-line ml-0.5" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </article>
  );
}